import { useMemo, useRef, useState } from 'react';
import { useApp } from '../app/store.ts';
import { archive, brauchtDeckblatt, kannDeckblatt, knownHashes, type ArchiveEntry, type Status } from '../archive/store.ts';
import { UNSICHER, type Progress } from '../archive/ocr.ts';
import { deriveTitle } from '../data/markdown.ts';
import { matches, parseQuery } from '../lib/search.ts';
import { Icon } from './Icon.tsx';

/**
 * Posteingang und Archiv (docs/KONZEPT.md, Abschnitt 19).
 *
 * Zwei Zustände, kein Ordnerbaum. Der Posteingang ist die Liste dessen, was noch
 * nicht angesehen wurde — leer heißt erledigt. Ins Archiv wandert nichts von selbst.
 *
 * Für den ersten großen Schwung sind Aufnehmen und Erkennen getrennt: Dreihundert
 * Dateien sind in einer Minute drin, ihre Erkennung dauert eine Stunde. Wer beides
 * verschränkt, kann keins von beidem abbrechen.
 */

/** Bis hierher wird beim Aufnehmen einzelner Dateien gleich mit erkannt. */
const AUTO_SEITEN = 5;
/** Darüber gilt es als Stapel: aufnehmen, und die Erkennung danach auf Zuruf. */
const STAPEL_AB = 5;


function formatDay(at: number | null): string {
  if (at === null) return 'ohne Datum';
  return new Date(at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

type Lauf = { gesamt: number; fertig: number; uebersprungen: number; gescheitert: string[]; was: string };

/** Posteingang, Archiv — und der Papierkorb, damit Weggeworfenes auffindbar bleibt. */
type Reiter = Status | 'papierkorb';

type Props = { onClose: () => void };

export function Archive({ onClose }: Props): JSX.Element {
  const { documents, refresh, open } = useApp();
  const [tab, setTab] = useState<Reiter>('posteingang');
  const [filter, setFilter] = useState('');
  const [aufnahme, setAufnahme] = useState<Lauf | null>(null);
  const [erkennung, setErkennung] = useState<Lauf | null>(null);
  const [deckblatt, setDeckblatt] = useState<Lauf | null>(null);
  const [bericht, setBericht] = useState<string | null>(null);
  const [ocr, setOcr] = useState<{ id: string; text: string } | null>(null);
  const [ziehen, setZiehen] = useState(false);
  const abbruch = useRef(false);

  // Weggeworfenes gehört in keine der beiden Listen — es blieb sonst stehen, als
  // wäre nichts geschehen, und ließ sich nicht einmal erneut aufnehmen.
  const lebend = documents.filter((entry) => entry.deletedAt === null);
  const inbox = lebend.filter((entry) => entry.dokument.status === 'posteingang');
  const stored = lebend.filter((entry) => entry.dokument.status === 'archiv');
  const geloescht = documents.filter((entry) => entry.deletedAt !== null);
  const offen = lebend.filter((entry) => entry.dokument.quelle === 'keine');
  // Nur was ein Blatt Papier ist und noch keins hat — sonst stünde der Hinweis ewig.
  const ohneBild = lebend.filter(brauchtDeckblatt);
  const query = useMemo(() => parseQuery(filter), [filter]);
  const listen: Record<Reiter, ArchiveEntry[]> = {
    posteingang: inbox,
    archiv: stored,
    papierkorb: geloescht,
  };
  const shown = listen[tab].filter((entry) => matches(entry, query));
  const laeuft = aufnahme !== null || erkennung !== null || deckblatt !== null;

  function report(id: string) {
    return (progress: Progress) =>
      setOcr({
        id,
        text: progress.pages > 1 ? `Seite ${progress.page} von ${progress.pages}` : 'Text wird erkannt …',
      });
  }

  /**
   * Nimmt auf, was hereingereicht wird — eine Datei oder ein ganzer Ordner. Bei einem
   * Stapel wird nicht nebenbei erkannt: Das würde den Lauf um Stunden verlängern und
   * ließe sich nicht getrennt anhalten.
   */
  async function take(list: FileList | File[] | null): Promise<void> {
    const files = [...(list ?? [])].filter((file) => file.size > 0);
    if (files.length === 0) return;

    abbruch.current = false;
    setBericht(null);
    const known = knownHashes(useApp.getState().documents);
    const stapel = files.length > STAPEL_AB;
    let uebersprungen = 0;
    const gescheitert: string[] = [];

    for (const [index, file] of files.entries()) {
      if (abbruch.current) break;
      setAufnahme({ gesamt: files.length, fertig: index, uebersprungen, gescheitert, was: file.name });
      try {
        const added = await archive.add(file, known, (stufe) =>
          setAufnahme({
            gesamt: files.length,
            fertig: index,
            uebersprungen,
            gescheitert,
            was: `${file.name} — ${stufe === 'lesen' ? 'wird gelesen' : 'wird hochgeladen'}`,
          }),
        );
        if ('duplicate' in added) {
          uebersprungen += 1;
        } else if (
          !stapel &&
          added.meta.quelle === 'keine' &&
          (added.meta.seiten ?? 1) <= AUTO_SEITEN
        ) {
          // Eigener Fang: Die Datei ist aufgenommen und liegt im Posteingang. Wenn
          // nur die Erkennung scheitert, ist das kein misslungenes Aufnehmen — es
          // bleibt der Knopf „Text erkennen".
          await archive
            .recognize(added.id, {
              bytes: added.bytes,
              hatDatum: added.meta.datum !== null,
              onProgress: report(added.id),
            })
            .catch((error) => console.warn('[archiv] Erkennung beim Aufnehmen:', error));
        }
      } catch (error) {
        const grund = error instanceof Error ? error.message : 'unbekannter Fehler';
        console.warn('[archiv] Aufnehmen gescheitert:', file.name, error);
        gescheitert.push(`${file.name} (${grund})`);
      }
      // Nicht nach jeder Datei: Ein Neuaufbau der Liste kostet bei dreihundert
      // Dokumenten mehr Zeit als das Aufnehmen selbst.
      if (index % 10 === 9) await refresh();
    }

    await refresh();
    setAufnahme(null);
    setOcr(null);
    setTab('posteingang');
    void archive.release();

    const teile = [`${files.length - uebersprungen - gescheitert.length} aufgenommen`];
    if (uebersprungen > 0) teile.push(`${uebersprungen} schon vorhanden`);
    if (abbruch.current) teile.push('abgebrochen');
    // Der Grund gehört dazu. „1 fehlgeschlagen" schickt jeden auf die falsche Fährte.
    if (gescheitert.length > 0) teile.push(`fehlgeschlagen: ${gescheitert.slice(0, 2).join('; ')}`);
    setBericht(teile.join(', '));
  }

  /** Arbeitet den Rückstand ab: alles ohne Text, eins nach dem anderen, anhaltbar. */
  async function erkenneAlles(): Promise<void> {
    abbruch.current = false;
    setBericht(null);
    const gescheitert: string[] = [];
    const warten = useApp
      .getState()
      .documents.filter((entry) => entry.dokument.quelle === 'keine' && entry.deletedAt === null);

    for (const [index, entry] of warten.entries()) {
      if (abbruch.current) break;
      setErkennung({
        gesamt: warten.length,
        fertig: index,
        uebersprungen: 0,
        gescheitert,
        was: deriveTitle(entry.content),
      });
      try {
        await archive.recognize(entry.id, {
          hatDatum: entry.dokument.datum !== null,
          onProgress: report(entry.id),
        });
      } catch (error) {
        // Eine unlesbare Datei hält den Rest nicht auf — aber verschwiegen wird sie
        // auch nicht. Ein Stapellauf, der stillschweigend Lücken lässt, ist wertlos.
        console.warn('[archiv] Erkennung fehlgeschlagen:', deriveTitle(entry.content), error);
        gescheitert.push(deriveTitle(entry.content));
      }
      if (index % 5 === 4) await refresh();
    }

    await refresh();
    setErkennung(null);
    setOcr(null);
    void archive.release();
    const wie = abbruch.current ? 'Erkennung angehalten' : 'Erkennung durch';
    setBericht(
      gescheitert.length > 0
        ? `${wie} — ${gescheitert.length} ohne Ergebnis: ${gescheitert.slice(0, 3).join(', ')}`
        : `${wie}.`,
    );
  }

  /**
   * Zeichnet Deckblätter nach. Beim Aufnehmen entsteht das Bild aus den Bytes, die
   * ohnehin zur Hand sind — misslingt es dort, blieb die Kachel bisher für immer
   * ohne Bild. Hier werden die Dateien einzeln geholt und die Bilder neu gezeichnet.
   *
   * `nurFehlende` trennt die beiden Anlässe: der Rückstand aus alten Aufnahmen, und
   * der Fall „das Bild stimmt nicht" — dann muss auch ersetzt werden, was schon da ist.
   */
  async function erneuereDeckblaetter(nurFehlende: boolean): Promise<void> {
    abbruch.current = false;
    setBericht(null);
    const gescheitert: string[] = [];
    let ohneBildErgebnis = 0;
    const warten = (nurFehlende ? ohneBild : listen[tab].filter(kannDeckblatt)).filter(
      (entry) => entry.deletedAt === null,
    );

    for (const [index, entry] of warten.entries()) {
      if (abbruch.current) break;
      setDeckblatt({
        gesamt: warten.length,
        fertig: index,
        uebersprungen: 0,
        gescheitert,
        was: deriveTitle(entry.content),
      });
      try {
        if (!(await archive.rethumb(entry.id))) ohneBildErgebnis += 1;
      } catch (error) {
        console.warn('[archiv] Deckblatt fehlgeschlagen:', deriveTitle(entry.content), error);
        gescheitert.push(deriveTitle(entry.content));
      }
      if (index % 5 === 4) await refresh();
    }

    await refresh();
    setDeckblatt(null);
    const wie = abbruch.current ? 'Angehalten' : 'Deckblätter erneuert';
    const teile = [`${wie} — ${warten.length - gescheitert.length} bearbeitet`];
    if (ohneBildErgebnis > 0) teile.push(`${ohneBildErgebnis} ohne Bild`);
    if (gescheitert.length > 0) teile.push(`fehlgeschlagen: ${gescheitert.slice(0, 2).join('; ')}`);
    setBericht(teile.join(', '));
  }

  /** Erkennung eines einzelnen Dokuments — für Nachzügler aus der Liste. */
  async function runOcr(id: string, hatDatum: boolean): Promise<void> {
    setBericht(null);
    setOcr({ id, text: 'Text wird erkannt …' });
    try {
      const ergebnis = await archive.recognize(id, { hatDatum, onProgress: report(id) });
      if (!ergebnis) setBericht('Aus dieser Datei war kein Text zu holen.');
      await refresh();
    } catch (error) {
      // Ein Knopf, der schweigend nichts tut, ist schlimmer als eine hässliche
      // Meldung. Der Grund steht hier, damit er auch berichtet werden kann.
      setBericht(`Erkennung fehlgeschlagen: ${error instanceof Error ? error.message : 'unbekannter Fehler'}`);
    } finally {
      setOcr(null);
      void archive.release();
    }
  }

  function fortschritt(lauf: Lauf, titel: string): JSX.Element {
    return (
      <div className="doc-run">
        <p>
          {titel} {lauf.fertig + 1} von {lauf.gesamt}
          {lauf.was && <em> — {lauf.was}</em>}
        </p>
        <div className="doc-bar">
          <span style={{ width: `${Math.round((lauf.fertig / Math.max(1, lauf.gesamt)) * 100)}%` }} />
        </div>
        {ocr && <p className="muted">{ocr.text}</p>}
        <button
          className="doc-file"
          onClick={() => {
            abbruch.current = true;
          }}
        >
          Anhalten
        </button>
      </div>
    );
  }

  /** Erste Zeilen des gewonnenen Textes — das Deckblatt für alles ohne Bild. */
  function textProbe(entry: ArchiveEntry): string {
    const text = entry.dokument.erkannt.replace(/\s+/g, ' ').trim();
    return text.slice(0, 220);
  }

  function kachel(entry: ArchiveEntry): JSX.Element {
    const meta = entry.dokument;
    return (
      <li key={entry.id} className="doc-tile">
        <button className="doc-cover" onClick={() => open(entry.id)} title={deriveTitle(entry.content)}>
          {meta.vorschau ? (
            <img src={meta.vorschau} alt="" />
          ) : (
            <span className="doc-cover-text">{textProbe(entry) || 'Ohne Vorschau'}</span>
          )}
          {(meta.unvollstaendig || meta.quelle === 'keine') && (
            <em className="doc-flag">{meta.unvollstaendig ? 'wird hochgeladen' : 'ohne Text'}</em>
          )}
        </button>

        <span className="doc-tile-title">{deriveTitle(entry.content)}</span>
        <span className="doc-tile-sub">
          {formatDay(meta.datum ?? meta.eingang)}
          {meta.konfidenz != null && meta.konfidenz < UNSICHER && <em> · unsicher erkannt</em>}
        </span>

        <div className="doc-tile-actions">
          {tab === 'papierkorb' ? (
            <>
              <button className="doc-file" onClick={() => void archive.restore(entry.id).then(refresh)}>
                Zurückholen
              </button>
              <button className="doc-file" onClick={() => void archive.purge(entry.id).then(refresh)}>
                Endgültig
              </button>
            </>
          ) : (
            <>
              {ocr?.id === entry.id ? (
                <span className="muted">{ocr.text}</span>
              ) : meta.quelle === 'keine' && !laeuft && !meta.unvollstaendig ? (
                <button className="doc-file" onClick={() => void runOcr(entry.id, meta.datum !== null)}>
                  Text erkennen
                </button>
              ) : (
                <button
                  className="doc-file"
                  onClick={() =>
                    void archive
                      .setStatus(entry.id, meta.status === 'posteingang' ? 'archiv' : 'posteingang')
                      .then(refresh)
                  }
                >
                  {meta.status === 'posteingang' ? 'Ablegen' : 'Zurück'}
                </button>
              )}
              <button
                className="doc-icon"
                title="In den Papierkorb"
                onClick={() => void archive.remove(entry.id).then(refresh)}
              >
                <Icon name="trash" size={16} />
              </button>
            </>
          )}
        </div>
      </li>
    );
  }

  return (
    <section
      className={ziehen ? 'archive doc-drop' : 'archive'}
      onDragOver={(event) => {
        event.preventDefault();
        setZiehen(true);
      }}
      onDragLeave={() => setZiehen(false)}
      onDrop={(event) => {
        event.preventDefault();
        setZiehen(false);
        void take(event.dataTransfer.files);
      }}
    >
      <header className="archive-head">
        <h2>Dokumente</h2>
        <div className="doc-tabs">
          <button
            className={tab === 'posteingang' ? 'chip chip-on' : 'chip'}
            onClick={() => setTab('posteingang')}
          >
            Posteingang ({inbox.length})
          </button>
          <button className={tab === 'archiv' ? 'chip chip-on' : 'chip'} onClick={() => setTab('archiv')}>
            Archiv ({stored.length})
          </button>
          {geloescht.length > 0 && (
            <button
              className={tab === 'papierkorb' ? 'chip chip-on' : 'chip'}
              onClick={() => setTab('papierkorb')}
            >
              Papierkorb ({geloescht.length})
            </button>
          )}
        </div>
        <input
          className="search archive-search"
          type="search"
          placeholder="In den Dokumenten suchen"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
        <button className="head-icon" onClick={onClose} title="Schließen">
          <Icon name="close" />
        </button>
      </header>

      <div className="archive-body">
        {!laeuft && (
          <div className="inline">
            {/* Der native Dateiknopf ist englisch beschriftet und nicht umbenennbar —
                also versteckt und mit eigener Schaltfläche davor. */}
            <label className="ghost file-label">
              Dateien wählen
              <input
                type="file"
                multiple
                onChange={(event) => {
                  void take(event.target.files);
                  event.target.value = '';
                }}
              />
            </label>
            <label className="ghost file-label">
              Ordner wählen
              <input
                type="file"
                multiple
                // Ganze Ordner samt Unterordnern; am Telefon gibt es das nicht, dort
                // bleibt der Knopf ohne Wirkung — deshalb steht er nicht allein.
                {...{ webkitdirectory: '', directory: '' }}
                onChange={(event) => {
                  void take(event.target.files);
                  event.target.value = '';
                }}
              />
            </label>
            <label className="ghost file-label">
              Foto aufnehmen
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(event) => {
                  void take(event.target.files);
                  event.target.value = '';
                }}
              />
            </label>
          </div>
        )}

        {aufnahme && fortschritt(aufnahme, 'Nehme auf:')}
        {erkennung && fortschritt(erkennung, 'Erkenne:')}
        {deckblatt && fortschritt(deckblatt, 'Deckblatt:')}
        {bericht && <p className="todos-hint">{bericht}</p>}

        {!laeuft && offen.length > 0 && (
          <p className="todos-hint">
            {offen.length} {offen.length === 1 ? 'Dokument hat' : 'Dokumente haben'} noch keinen
            Text.{' '}
            <button className="capture-undo" onClick={() => void erkenneAlles()}>
              Erkennung starten
            </button>
          </p>
        )}

        {!laeuft && ohneBild.length > 0 && (
          <p className="todos-hint">
            {ohneBild.length}{' '}
            {ohneBild.length === 1 ? 'Dokument hat kein' : 'Dokumente haben kein'} Deckblatt.{' '}
            <button className="capture-undo" onClick={() => void erneuereDeckblaetter(true)}>
              Deckblätter erzeugen
            </button>
          </p>
        )}

        {!laeuft && tab !== 'papierkorb' && listen[tab].some(kannDeckblatt) && (
          <p className="todos-hint">
            Stimmt ein Vorschaubild nicht, lässt es sich neu zeichnen — für dieses Fach
            auf einmal, oder einzeln im geöffneten Dokument.{' '}
            <button className="capture-undo" onClick={() => void erneuereDeckblaetter(false)}>
              Alle Deckblätter neu erzeugen
            </button>
          </p>
        )}

        <ul className="doc-grid">{shown.map(kachel)}</ul>

        {shown.length === 0 && (
          <p className="empty">
            {filter
              ? 'Nichts gefunden.'
              : tab === 'posteingang'
                ? 'Nichts liegt an.'
                : tab === 'archiv'
                  ? 'Noch nichts abgelegt.'
                  : 'Der Papierkorb ist leer.'}
          </p>
        )}

        <p className="hint">
          Ganze Ordner lassen sich auch hierher ziehen. Dabei wird nur aufgenommen —
          die Texterkennung startest du danach, damit sie sich anhalten lässt. Dieselbe
          Datei ein zweites Mal wird übersprungen.
        </p>
      </div>
    </section>
  );
}
